import { Box, Divider, Text, UnstyledButton } from '@mantine/core'
import type { DistrictWithBoundaries } from '@/api/main/types.gen'
import { districtLabel, splitSelectedFirst } from '@/utils/autoScheduleDistricts'
import { useTranslation } from 'react-i18next'
import { memo, useMemo } from 'react'
import clsx from 'clsx'
import classes from './AutoScheduleSelection.module.css'

const DistrictRow = memo(function DistrictRow({
  district,
  selected,
  hovered,
  onToggle,
  onHover,
}: {
  district: DistrictWithBoundaries
  selected: boolean
  hovered: boolean
  onToggle: (districtId: number) => void
  onHover: (districtId: number | undefined) => void
}) {
  const label = districtLabel(district)
  return (
    <UnstyledButton
      onClick={() => onToggle(district.id)}
      onMouseEnter={() => onHover(district.id)}
      onMouseLeave={() => onHover(undefined)}
      onFocus={() => onHover(district.id)}
      onBlur={() => onHover(undefined)}
      w="100%"
      px="sm"
      py="xs"
      aria-pressed={selected}
      aria-label={label}
      className={clsx(classes.row, hovered && classes.hovered, selected && classes.selected)}
      style={{ display: 'block', cursor: 'pointer' }}
    >
      <Text size="sm">{label}</Text>
    </UnstyledButton>
  )
})

export function DistrictsTable({
  districts,
  selectedIds,
  hoveredId,
  onToggle,
  onHover,
}: {
  districts: DistrictWithBoundaries[]
  selectedIds: number[]
  hoveredId: number | undefined
  onToggle: (districtId: number) => void
  onHover: (districtId: number | undefined) => void
}) {
  const { t } = useTranslation('scheduling')
  const selectedSet = useMemo(() => new Set(selectedIds), [selectedIds])
  const sorted = useMemo(
    () => [...districts].sort((a, b) => districtLabel(a).localeCompare(districtLabel(b))),
    [districts]
  )
  const { selected, rest } = splitSelectedFirst(sorted, district => selectedSet.has(district.id))

  return (
    <Box style={{ minHeight: 0, flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <Text size="xs" c="dimmed" px="sm" pb="xs">
        {t('autoSchedule.districtsSelected', { count: selectedIds.length })}
      </Text>
      <Box style={{ overflow: 'auto', minHeight: 0, flex: 1 }}>
        {selected.map(district => (
          <DistrictRow
            key={district.id}
            district={district}
            selected
            hovered={hoveredId === district.id}
            onToggle={onToggle}
            onHover={onHover}
          />
        ))}
        {selected.length > 0 && rest.length > 0 ? <Divider my={6} /> : null}
        {rest.map(district => (
          <DistrictRow
            key={district.id}
            district={district}
            selected={false}
            hovered={hoveredId === district.id}
            onToggle={onToggle}
            onHover={onHover}
          />
        ))}
      </Box>
    </Box>
  )
}
